/**
 * User Permissions Query Hook
 * Fetches user permissions and syncs with authStore
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../api/users';
import { useAuthStore } from '../stores/authStore';
import { queryKeys } from '../lib/queryClient';

export function useUserPermissions(userId: string | undefined) {
  const query = useQuery({
    queryKey: queryKeys.users.permissions(userId!),
    queryFn: () => usersApi.getPermissions(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - permissions don't change often
  });

  useEffect(() => {
    if (query.data) {
      const permissions = query.data;
      const programAdminOrOwnerFor = permissions.programsAdmin.map(p => p.programId);
      const mentorFor = permissions.cohortsMentor.map(m => ({
        programId: '',
        cohortId: m.cohortId
      }));
      const participantIn = permissions.cohortsEnrolled
        .filter(e => e.status === 'enrolled' || e.status === 'graduated')
        .map(e => ({
          programId: '',
          cohortId: e.cohortId,
          status: e.status,
          progress: 0
        }));
      useAuthStore.getState().updatePermissions({ programAdminOrOwnerFor, mentorFor, participantIn });
    }
  }, [query.data]);

  return query;
}
